// `label` is the phrase to show once actBy is too far out to count down ("due Wed",
// "ready 1h"). Once actBy is close, the client takes over and shows a countdown instead.
// An actBy that is a date with no time ("2026-01-15") is an all-day due: it is labelled
// from the viewer's own calendar, due until local midnight, and the server label is unused.
// The host row uses this same formatter.

import { formatFullDateTime } from './localTime';

export type UrgencyTone = 'default' | 'soon' | 'late';

export type UrgencyInput = {
  label: string;
  actBy?: string;
  tone?: UrgencyTone;
};

export type Urgency = {
  label: string;
  tone: UrgencyTone;
};

// How close actBy has to be before the label switches to a minute countdown; not fixed
// elsewhere, so 60 minutes covers the last hour before a deadline.
export const URGENCY_SOON_MINUTES = 60;

export function resolveUrgency(
  { label, actBy, tone = 'default' }: UrgencyInput,
  now: Date = new Date()
): Urgency {
  if (!actBy) return { label, tone };

  const day = localDayOf(actBy);
  if (day) return allDayUrgency(day, now);

  const minutesLeft = Math.round(
    (new Date(actBy).getTime() - now.getTime()) / 60_000
  );
  if (minutesLeft <= 0) return { label: 'past due', tone: 'late' };
  if (minutesLeft <= URGENCY_SOON_MINUTES) {
    return { label: `${minutesLeft} min left`, tone: 'soon' };
  }

  return { label, tone };
}

const DAY_MS = 24 * 60 * 60_000;
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

// The hover title: the date alone for an all-day due, else the full local date and
// time on the viewer's chosen clock
export function urgencyTitle(
  actBy: string,
  prefer12Hr: boolean | null
): string {
  return allDayTitle(actBy) ?? formatFullDateTime(actBy, prefer12Hr);
}

export function allDayTitle(actBy: string): string | undefined {
  return localDayOf(actBy)?.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// Local midnight on the given date; `new Date('2026-01-15')` would read it as UTC midnight
function localDayOf(actBy: string): Date | null {
  const match = DATE_ONLY.exec(actBy);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

// The server's due labels, counted on the viewer's calendar instead of the stored timezone
function allDayUrgency(day: Date, now: Date): Urgency {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Rounded, since a day that crosses a daylight-saving change is 23 or 25 hours long
  const daysLeft = Math.round((day.getTime() - today.getTime()) / DAY_MS);
  if (daysLeft < 0) return { label: 'past due', tone: 'late' };
  if (daysLeft === 0) return { label: 'due today', tone: 'soon' };
  if (daysLeft === 1) return { label: 'due tomorrow', tone: 'default' };
  const name =
    daysLeft < 7
      ? day.toLocaleDateString('en-US', { weekday: 'short' })
      : day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return { label: `due ${name}`, tone: 'default' };
}
